import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { ChevronLeft, TrendingUp, AlertTriangle, HardHat, Package, Wallet, Printer, FileText, ShoppingCart } from "lucide-react";
import api from "../api/axiosInstance";

const ProjectProfitability = ({ projectId, onBack }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const res = await api.get(`get_project_profitability.php?project_id=${projectId}`);
        setReport(res.data.details);
      } catch (err) {
        console.error("Profitability Audit Failed:", err);
      } finally {
        setLoading(false);
      }
    };
    if (projectId) fetchReport();
  }, [projectId]);

  const downloadPDFReport = async () => {
    try {
      const response = await api.get(`generate_profitability_pdf.php?project_id=${projectId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Profitability_Report_${report.project_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Failed to generate PDF report.");
    }
  };
  const downloadMasterReport = async () => {
    try {
      const response = await api.get(`/generate_master_project_report.php?project_id=${projectId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Master_Audit_Report_Project_${projectId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Master Report Generation Failed.");
    }
  };
  if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-400 uppercase tracking-widest">Calculating Site Margins...</div>;
  if (!report) return <div className="p-20 text-center"><p>Data unavailable.</p><button onClick={onBack}>Back</button></div>;

  const totalCost = report.costs.reduce((acc, curr) => acc + curr.value, 0);
  const profit = report.revenue - totalCost;
  const marginPercent = report.revenue > 0 ? ((profit / report.revenue) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-8 animate-in slide-in-from-right duration-500">
      {/* Header */}
      <div className="flex justify-between items-center px-2">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 font-black text-[10px] uppercase hover:text-slate-900 transition-all group">
          <div className="p-2 bg-white rounded-full shadow-sm group-hover:bg-slate-100"><ChevronLeft size={16} /></div>
          Back to Project List
        </button>
        <div className="flex gap-3">
          <button onClick={downloadPDFReport} className="bg-indigo-600 text-white px-6 py-2 rounded-full text-[10px] font-black uppercase flex items-center gap-2 shadow-lg hover:bg-indigo-700">
            <Printer size={14} /> Export Audit PDF
          </button>
          <button
            onClick={downloadMasterReport}
            className="bg-slate-900 text-white px-8 py-4 rounded-[24px] font-black text-xs uppercase flex items-center gap-3 shadow-2xl hover:scale-105 transition-all"
          >
            <FileText size={18} /> Download Master Project Audit
          </button>
          <span className="bg-slate-900 text-white px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center">
            Live Audit: {report.project_name}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Visual Breakdown Card */}
        <div className="lg:col-span-2 bg-white p-10 rounded-[48px] border border-slate-100 shadow-sm">
          <h2 className="text-3xl font-black italic tracking-tighter uppercase mb-2 text-slate-900">Cost Breakdown</h2>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={report.costs} innerRadius={100} outerRadius={150} paddingAngle={8} dataKey="value" stroke="none">
                  {report.costs.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: "24px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} />
                <Legend verticalAlign="middle" align="right" layout="vertical" iconType="circle" wrapperStyle={{ fontWeight: "800", textTransform: "uppercase", fontSize: "11px" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Profitability Gauge & Detail List */}
        <div className="space-y-6">
          <div className={`p-10 rounded-[48px] text-white shadow-2xl relative overflow-hidden ${profit >= 0 ? "bg-emerald-600" : "bg-rose-600"}`}>
            <p className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-2">Net Project Profit</p>
            <h3 className="text-4xl font-black italic tracking-tighter">₹{profit.toLocaleString()}</h3>
            <div className="mt-6 flex items-center justify-between">
              <span className="text-xs font-black uppercase bg-white/20 px-3 py-1 rounded-lg">Margin: {marginPercent}%</span>
              <TrendingUp className="opacity-30" size={48} />
            </div>
          </div>

          <div className="bg-white p-8 rounded-[40px] border border-slate-100 space-y-4 shadow-sm">
            {/* Direct Site Buys */}
            <div className="flex justify-between items-center p-5 bg-slate-50 rounded-3xl">
              <div className="flex items-center gap-3">
                <ShoppingCart className="text-orange-500" size={18} />
                <span className="text-[10px] font-black uppercase text-slate-500">Direct Site Buys</span>
              </div>
              <span className="font-black text-slate-900">₹{report.costs.find(c => c.name === "Direct Site Buys")?.value.toLocaleString() || 0}</span>
            </div>

            {/* Labour / Overheads */}
            <div className="flex justify-between items-center p-5 bg-slate-50 rounded-3xl">
              <div className="flex items-center gap-3">
                <HardHat className="text-rose-500" size={18} />
                <span className="text-[10px] font-black uppercase text-slate-500">Labour / Overheads</span>
              </div>
              <span className="font-black text-slate-900">₹{report.costs.find(c => c.name === "Labour")?.value.toLocaleString() || 0}</span>
            </div>

            {/* Site Petty Cash */}
            <div className="flex justify-between items-center p-5 bg-slate-50 rounded-3xl">
              <div className="flex items-center gap-3">
                <Wallet className="text-emerald-500" size={18} />
                <span className="text-[10px] font-black uppercase text-slate-500">Site Petty Cash</span>
              </div>
              <span className="font-black text-slate-900">₹{report.costs.find(c => c.name === "Petty Cash")?.value.toLocaleString() || 0}</span>
            </div>

            {/* Vendor Materials */}
            <div className="flex justify-between items-center p-5 bg-slate-50 rounded-3xl">
              <div className="flex items-center gap-3">
                <Package className="text-indigo-500" size={18} />
                <span className="text-[10px] font-black uppercase text-slate-500">Vendor Materials</span>
              </div>
              <span className="font-black text-slate-900">₹{report.costs.find(c => c.name === "Vendor Materials")?.value.toLocaleString() || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectProfitability;